import { IDVInformation, VerifiedClient } from '../../../types';
import { evaluateIDVResult, extractIDVInformation, getAutoFailReasons, isPastTargetDate, isScoreAlreadyZero } from './IDVUtils';
import lddConnectLogo from '../../../../../images/LDD-Connect-Logo.png';
import clientConnectLogo from '../../../../../images/cc-full.png';

const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
        img.src = src;
    });
};

const loadJSPDF = (): Promise<any> => {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
        script.onload = () => {
            const jsPDF = (window as any).jspdf.jsPDF;
            if (jsPDF) {
                resolve(jsPDF);
            } else {
                reject(new Error("Failed to load jsPDF"));
            }
        };
        script.onerror = () => reject(new Error(`Failed to load script: ${script.src}`));
        document.body.appendChild(script);
    });
};

export const renderDownloadPDF = async (verifiedClients: VerifiedClient[], province: string, fileNumber: string) => {
    const jsPDF = await loadJSPDF();
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const config = {
        margin: 20,
        imageWidth: 80,
        imageHeight: 60,
        scoresYOffset: 6,
        pageEndYOffset: 40,
        scoresPositionYOffset: 17,
        pageHeight: doc.internal.pageSize.height,
        pageWidth: doc.internal.pageSize.width
    }
    let yOffset = config.margin;

    doc.setFontSize(20);
    doc.setFont(undefined, 'bold')
    doc.text(`Client ID Details for File: ${fileNumber}`, config.margin, yOffset).setFont(undefined, "normal");
    yOffset += 20;

    const addClientDetails = (client) => {
        doc.setFontSize(14);

        const leftFirstLineY = yOffset;
        doc.setFont(undefined, 'bold');
        doc.text('Name:', config.margin, yOffset);
        const nameWidth = doc.getTextWidth('Name: ');
        doc.setFont(undefined, 'normal');
        const maxWidth = (province !== 'AB' ? pageWidth/2 : (isPastTargetDate() ? pageWidth/2 : pageWidth)) - config.margin * 2 - nameWidth;
        const wrappedName = doc.splitTextToSize(client.clientInformation.Name, maxWidth);
        doc.text(wrappedName, config.margin + nameWidth, yOffset);
        yOffset += (wrappedName.length+1) * 5;

        doc.setFont(undefined, 'bold');
        doc.text('ID Type:', config.margin, yOffset);
        const idTypeWidth = doc.getTextWidth('ID Type: ');
        doc.setFont(undefined, 'normal');
        doc.text(client.clientInformation.Type, config.margin + idTypeWidth, yOffset);
        yOffset += 10;

        doc.setFont(undefined, 'bold');
        doc.text('Face Verification Passed:', config.margin, yOffset);
        const faceVerificationResultWidth = doc.getTextWidth('Face Verification Passed: ');
        doc.setFont(undefined, 'normal');
        doc.text(client.FaceVerificationPassed ? 'Yes' : 'No', config.margin + faceVerificationResultWidth, yOffset);
        yOffset += 10;

        // Add auto-fail reasons if applicable
        const autoFailReasons = getAutoFailReasons(client);
        const shouldShowAutoFail = autoFailReasons.length > 0 && !isScoreAlreadyZero(client);
        const shouldShowAutoFailSection = shouldShowAutoFail && ((isPastTargetDate() && province === "AB") || (province !== 'AB'));
        
        if (shouldShowAutoFailSection) {
            autoFailReasons.forEach((reason) => {
                doc.setFont(undefined, 'bold');
                doc.setTextColor(220, 53, 69); 
                doc.text('This ID has been automatically failed.', config.margin, yOffset);
                yOffset += 8;
                
                const reasonLabelWidth = doc.getTextWidth('Reason: ');
                doc.setFont(undefined, 'bold');
                doc.text('Reason:', config.margin, yOffset);
                doc.setFont(undefined, 'normal');
                doc.text(reason, config.margin + reasonLabelWidth, yOffset);
                yOffset += 10;
            });
            doc.setTextColor(0, 0, 0); 
        }

        const addLabeledValue = (doc: any, label: string, value: string, x: number, y: number) => {
            doc.setFont(undefined, 'bold');
            doc.text(label, x, y);
            const labelWidth = doc.getTextWidth(label);
            doc.setFont(undefined, 'normal');
            doc.text(value, x + labelWidth, y);
            return doc.getTextDimensions(value).h;
        };
        if ((isPastTargetDate() && province === "AB") || (province !== 'AB')) {
            const rightPartY = leftFirstLineY;
            const baseX = config.margin + config.imageWidth + 10;
        
            const labels = [
                { text: 'Certainty: ', value: shouldShowAutoFail ? 'HIGH' : (client.clientIdFraudCheck.certainty === 'NotPerformed' ? 'Low' : client.clientIdFraudCheck.certainty) },
                { text: 'Score: ', value: shouldShowAutoFail ? '0%' : client.clientIdFraudCheck.score },
                { text: 'Failed Checks: ', value: client.clientIdFraudCheck.failedChecks },
                { text: 'Checks not performed: ', value: client.clientIdFraudCheck.notPerformedChecks },
                { text: 'Passed Checks: ', value: client.clientIdFraudCheck.passedChecks },
                { text: 'Total Checks: ', value: client.clientIdFraudCheck.totalChecks }
            ];
        
            let rightY = rightPartY;
            labels.forEach(item => {
                const textHeight = addLabeledValue(doc, item.text, item.value.toString(), baseX, rightY);
                rightY += Math.max(textHeight, config.scoresYOffset);
            });
            const spacingBelowScores = 12;
            yOffset = Math.max(yOffset, rightY + spacingBelowScores);
        }

        const frontImage = client.clientIDImages.frontImage;
        const backImage = client.clientIDImages.backImage;

        if (frontImage || backImage) {
            if (yOffset + config.imageHeight > config.pageHeight) {
                doc.addPage();
                yOffset = config.margin;
            }

            if (frontImage) {
                doc.addImage(frontImage, 'PNG', config.margin, yOffset, config.imageWidth, config.imageHeight);
            } else {
                doc.text('Front Image Not Available', config.margin, yOffset + config.imageHeight / 2);
            }

            if (backImage) {
                doc.addImage(backImage, 'PNG', config.margin + config.imageWidth + 10, yOffset, config.imageWidth, config.imageHeight);
            } else {
                doc.text('Back Image Not Available', config.margin + config.imageWidth + 10, yOffset + config.imageHeight / 2);
            }

            yOffset += config.imageHeight + 10;
        } else {
            yOffset += 10;
        }

        doc.setDrawColor(0);
        doc.setLineWidth(0.5); 
        doc.line(config.margin, yOffset, pageWidth - config.margin, yOffset); 
        yOffset += 10; 
    };

    verifiedClients.forEach((client) => {
        if (yOffset + config.pageEndYOffset > config.pageHeight) {
            doc.addPage();
            yOffset = config.margin;
        }
        addClientDetails(client);
    });

    doc.save(`Client_ID_Details_${fileNumber}.pdf`);
};

const ID_INFO_ROWS: { key: keyof IDVInformation; label: string }[] = [
    { key: 'givenName', label: 'Given Name' },
    { key: 'surname', label: 'Surname' },
    { key: 'sex', label: 'Sex' },
    { key: 'idType', label: 'ID Type' },
    { key: 'issuingJurisdiction', label: 'Issuing Jurisdiction' },
    { key: 'documentNumber', label: 'Document Number' },
    { key: 'dateOfExpiry', label: 'Date of Expiry' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
];

const formatPdfDate = (input: string | undefined): string => {
    if (!input) return '';
    const d = new Date(input.endsWith('Z') ? input : `${input}Z`);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

export type IDVResultPDFOptions = {
    fileNumber?: string;
    sourceFirm?: string;
    dateRequested?: string;
    /** 'save' (default) downloads the PDF; 'print' opens it in a new tab with the print dialog. */
    mode?: 'save' | 'print';
};

/**
 * Render the post-reconfig (#19619) per-record IDV result PDF. Two-section layout matching the
 * on-screen card: Pass/Fail result with disclaimer + ID images on page 1 alongside ID Information
 * and Request Details. When the verification has a selfie image, a second page is appended that
 * shows the selfie with the same Pass/Fail card.
 */
export const renderIDVResultPDF = async (client: VerifiedClient, options: IDVResultPDFOptions = {}) => {
    const [jsPDF, lddLogoImg, ccLogoImg] = await Promise.all([
        loadJSPDF(),
        loadImage(lddConnectLogo).catch(() => null),
        loadImage(clientConnectLogo).catch(() => null),
    ]);
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    const evaluation = evaluateIDVResult(client);
    const information = extractIDVInformation(client);
    const showSelfiePage = !client.skipFaceVerification && !!client.clientIDImages?.verifyFaceFrame;
    const totalPages = showSelfiePage ? 2 : 1;

    const dateReceived = formatPdfDate(client.clientInformation?.Timestamp);
    const dateDownloaded = formatPdfDate(new Date().toISOString());

    const PASS_COLOR: [number, number, number] = [40, 167, 69];
    const FAIL_COLOR: [number, number, number] = [217, 83, 79];
    const ACCENT_BG: [number, number, number] = [240, 247, 251];
    const ALERT_BG: [number, number, number] = [248, 215, 218];
    const ALERT_FG: [number, number, number] = [114, 28, 36];

    const drawCommonTop = () => {
        const logoHeight = 14;
        let cursorX = margin;
        if (lddLogoImg) {
            const w = logoHeight * (lddLogoImg.naturalWidth / lddLogoImg.naturalHeight);
            doc.addImage(lddLogoImg, 'PNG', cursorX, margin - 4, w, logoHeight);
            cursorX += w + 6;
        }
        if (ccLogoImg) {
            const w = logoHeight * (ccLogoImg.naturalWidth / ccLogoImg.naturalHeight);
            doc.addImage(ccLogoImg, 'PNG', cursorX, margin - 4, w, logoHeight);
        }

        const bannerY = margin + logoHeight + 2;
        doc.setFillColor(...ACCENT_BG);
        doc.rect(margin, bannerY, contentWidth, 9, 'F');
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('ID Verification Results for:', margin + 3, bannerY + 6);
        doc.setFont(undefined, 'normal');
        doc.text(client.clientInformation?.Name || '', margin + 65, bannerY + 6);
    };

    /**
     * Draws an image preserving its native aspect ratio, fit inside (maxW × maxH) and optionally
     * centered horizontally within that box. Without this, jsPDF stretches images to whatever
     * dimensions you pass — which distorted the portrait selfie into a landscape rectangle.
     */
    const drawImageFitted = (
        imageData: string,
        x: number,
        y: number,
        maxW: number,
        maxH: number,
        align: 'left' | 'center' = 'left',
    ) => {
        try {
            const props = doc.getImageProperties(imageData);
            const ratio = props.width / props.height;
            let w = maxW;
            let h = maxW / ratio;
            if (h > maxH) {
                h = maxH;
                w = maxH * ratio;
            }
            const xPos = align === 'center' ? x + (maxW - w) / 2 : x;
            doc.addImage(imageData, 'PNG', xPos, y, w, h);
        } catch {
            // Fallback if jsPDF can't read image props — better to render stretched than to crash.
            doc.addImage(imageData, 'PNG', x, y, maxW, maxH);
        }
    };

    const drawResultCard = (yStart: number, cardWidth: number) => {
        const padX = 4;
        const padTop = 6;
        const padBottom = 5;
        const titleHeight = 5;
        const disclaimer =
            "Disclaimer: The ClientConnect ID Verification can produce a high quality analysis of an ID document and is provided with the understanding that LDD is not engaged in rendering legal or other professional advice. You should always rely on your own professional judgment and experience in assessing the authenticity of an ID document.";

        // Individual face verification result — shown only when face verification was performed.
        const showFaceResult = !client.skipFaceVerification;

        doc.setFontSize(8);
        doc.setFont(undefined, 'italic');
        const disclaimerLines = doc.splitTextToSize(disclaimer, cardWidth - padX * 2);
        const disclaimerHeight = disclaimerLines.length * 3.5;

        const alerts: string[] = [];
        if (evaluation.outcome === 'Fail' && evaluation.failMessage) alerts.push(evaluation.failMessage);
        if (evaluation.faceVerificationFailed && evaluation.faceMismatchMessage) alerts.push(evaluation.faceMismatchMessage);
        const alertWrapped = alerts.map(t => doc.splitTextToSize(t, cardWidth - padX * 2 - 4));
        const alertsHeight = alertWrapped.reduce((acc, w) => acc + w.length * 4 + 4 + 2, 0);

        const faceLineHeight = showFaceResult ? titleHeight : 0;
        const totalHeight = padTop + titleHeight + faceLineHeight + 3 + disclaimerHeight + alertsHeight + padBottom;

        // Card background — entire card, not just the title bar.
        doc.setFillColor(...ACCENT_BG);
        doc.rect(margin, yStart, cardWidth, totalHeight, 'F');

        let cursorY = yStart + padTop;

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('ID Verification Result:', margin + padX, cursorY);
        const labelWidth = doc.getTextWidth('ID Verification Result: ');
        doc.setTextColor(...(evaluation.outcome === 'Pass' ? PASS_COLOR : FAIL_COLOR));
        doc.text(evaluation.outcome, margin + padX + labelWidth, cursorY);
        doc.setTextColor(0, 0, 0);
        cursorY += titleHeight;

        if (showFaceResult) {
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text('Face Verification Result:', margin + padX, cursorY);
            const faceLabelWidth = doc.getTextWidth('Face Verification Result: ');
            doc.setTextColor(...(client.FaceVerificationPassed ? PASS_COLOR : FAIL_COLOR));
            doc.text(client.FaceVerificationPassed ? 'Pass' : 'Fail', margin + padX + faceLabelWidth, cursorY);
            doc.setTextColor(0, 0, 0);
            cursorY += titleHeight;
        }

        doc.setFontSize(8);
        doc.setFont(undefined, 'italic');
        doc.text(disclaimerLines, margin + padX, cursorY + 3);
        cursorY += disclaimerHeight + 3;

        for (const wrapped of alertWrapped) {
            const ah = wrapped.length * 4 + 4;
            doc.setFillColor(...ALERT_BG);
            doc.rect(margin + padX, cursorY, cardWidth - padX * 2, ah, 'F');
            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(...ALERT_FG);
            doc.text(wrapped, margin + padX + 2, cursorY + 4);
            doc.setTextColor(0, 0, 0);
            cursorY += ah + 2;
        }

        return yStart + totalHeight;
    };

    const drawTwoColumnTable = (
        yStart: number,
        leftTitle: string,
        leftRows: { label: string; value: string }[],
        rightTitle: string,
        rightRows: { label: string; value: string }[],
    ) => {
        const colWidth = contentWidth / 2 - 3;
        const titleHeight = 7;
        const titleGap = 5;
        const rowHeight = 6;
        const bottomPad = 4;

        const drawColumn = (xStart: number, title: string, rows: { label: string; value: string }[]) => {
            const totalHeight = titleHeight + titleGap + rows.length * rowHeight + bottomPad;
            // Background covers the entire card, not just the title bar.
            doc.setFillColor(...ACCENT_BG);
            doc.rect(xStart, yStart, colWidth, totalHeight, 'F');

            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(title, xStart + 3, yStart + 5);

            let rowY = yStart + titleHeight + titleGap;
            doc.setFontSize(9);
            for (const row of rows) {
                doc.setFont(undefined, 'normal');
                doc.text(`${row.label}:`, xStart + 3, rowY);
                doc.setFont(undefined, 'bold');
                doc.text(row.value, xStart + colWidth / 2, rowY);
                rowY += rowHeight;
            }
            return yStart + totalHeight;
        };

        const leftEndY = drawColumn(margin, leftTitle, leftRows);
        const rightEndY = drawColumn(margin + colWidth + 6, rightTitle, rightRows);
        return Math.max(leftEndY, rightEndY);
    };

    const drawPageFooter = (pageNumber: number, footnote?: string) => {
        if (footnote) {
            doc.setFontSize(8);
            doc.setFont(undefined, 'italic');
            doc.setTextColor(80, 80, 80);
            doc.text(footnote, margin, pageHeight - 12);
            doc.setTextColor(0, 0, 0);
        }
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text(`Page ${pageNumber} of ${totalPages}`, margin, pageHeight - 6);
    };

    // ----- Page 1 -----
    drawCommonTop();
    let yOffset = margin + 31;

    const cardWidth = contentWidth * 0.55;
    const imagesX = margin + cardWidth + 6;
    const imagesWidth = contentWidth - cardWidth - 6;
    const cardEndY = drawResultCard(yOffset, cardWidth);

    const imageHeight = 40;
    if (client.clientIDImages?.frontImage) {
        drawImageFitted(client.clientIDImages.frontImage, imagesX, yOffset, imagesWidth, imageHeight, 'center');
    }
    if (client.clientIDImages?.backImage) {
        drawImageFitted(client.clientIDImages.backImage, imagesX, yOffset + imageHeight + 4, imagesWidth, imageHeight, 'center');
    }

    yOffset = Math.max(cardEndY, yOffset + imageHeight * 2 + 4) + 6;

    const idInfoRows = ID_INFO_ROWS
        .map(row => ({ label: row.label, value: information[row.key] || '' }))
        .filter(row => row.value);

    const requestRows = [
        { label: 'Source Firm', value: options.sourceFirm || '' },
        { label: 'File #', value: options.fileNumber || '' },
        { label: 'Date Requested', value: options.dateRequested || '' },
        { label: 'Date Received', value: dateReceived },
        { label: 'Date Downloaded', value: dateDownloaded },
    ].filter(row => row.value);

    drawTwoColumnTable(yOffset, 'ID Information:', idInfoRows, 'Request Details:', requestRows);

    drawPageFooter(1, showSelfiePage ? '*Selfie image is on page 2.' : undefined);

    // ----- Page 2 (selfie) -----
    if (showSelfiePage) {
        doc.addPage();
        drawCommonTop();
        let p2 = margin + 31;
        const p2CardWidth = contentWidth * 0.45;
        drawResultCard(p2, p2CardWidth);

        const selfieX = margin + p2CardWidth + 6;
        const selfieWidth = contentWidth - p2CardWidth - 6;
        const selfieHeight = 110;
        if (client.clientIDImages?.verifyFaceFrame) {
            drawImageFitted(client.clientIDImages.verifyFaceFrame, selfieX, p2, selfieWidth, selfieHeight, 'center');
        }

        drawPageFooter(2, '*ID images are on page 1.');
    }

    const safeName = (client.clientInformation?.Name || 'Client').replace(/[^A-Za-z0-9_-]+/g, '_');
    if (options.mode === 'print') {
        doc.autoPrint();
        const blobUrl = doc.output('bloburl');
        const printWindow = window.open(blobUrl);
        if (!printWindow) {
            // Pop-up was blocked — fall back to save so the user can open and print manually.
            doc.save(`IDV_Result_${safeName}.pdf`);
        }
    } else {
        doc.save(`IDV_Result_${safeName}.pdf`);
    }
};